import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Columns2, List, Search, Sheet } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';

import { loraApi } from '@/api/connectivity/lora';
import { DeviceActivationTable } from '@/components/connectivity/device-activation-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { TableSkeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface SearchFormData {
  serial: string;
  model: string;
  view: 'column' | 'list';
}

export function LoraActivationPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get params from URL
  const serial = searchParams.get('serial') || '';
  const model = searchParams.get('model') || '';
  const view = (searchParams.get('view') as 'column' | 'list') || 'column';
  const activatedPage = Number(searchParams.get('activatedPage')) || 1;
  const deactivatedPage = Number(searchParams.get('deactivatedPage')) || 1;
  const limit = 20;

  // Form setup
  const form = useForm<SearchFormData>({
    defaultValues: {
      serial,
      model,
      view,
    },
  });

  // Fetch technologies for model select
  const { data: technologies } = useQuery({
    queryKey: ['technologies'],
    queryFn: () => loraApi.findTechnologies(),
  });

  // Fetch activated devices
  const { data: activatedDevices, isLoading: isLoadingActivated } = useQuery({
    queryKey: ['devices', 'activated', activatedPage, serial, model],
    queryFn: () =>
      loraApi.findActivatedDevices({
        page: activatedPage,
        limit,
        serial: serial || undefined,
        model: model || undefined,
      }),
  });

  // Fetch deactivated devices
  const { data: deactivatedDevices, isLoading: isLoadingDeactivated } = useQuery({
    queryKey: ['devices', 'deactivated', deactivatedPage, serial, model],
    queryFn: () =>
      loraApi.findDeactivatedDevices({
        page: deactivatedPage,
        limit,
        serial: serial || undefined,
        model: model || undefined,
      }),
  });

  const total = (activatedDevices?.total ?? 0) + (deactivatedDevices?.total ?? 0);

  const handleSearch = form.handleSubmit((data) => {
    const params = new URLSearchParams(searchParams);
    params.set('activatedPage', '1');
    params.set('deactivatedPage', '1');

    if (data.serial) {
      params.set('serial', data.serial);
    } else {
      params.delete('serial');
    }

    if (data.model) {
      params.set('model', data.model);
    } else {
      params.delete('model');
    }

    setSearchParams(params);
  });

  const handleClearFilters = () => {
    form.reset({ serial: '', model: '', view });
    const params = new URLSearchParams();
    params.set('view', view);
    setSearchParams(params);
  };

  const handleViewChange = (newView: string) => {
    if (!newView) return;
    const params = new URLSearchParams(searchParams);
    params.set('view', newView);
    setSearchParams(params);
    form.setValue('view', newView as 'column' | 'list');
  };

  const handleActivatedPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('activatedPage', String(page));
    setSearchParams(params);
  };

  const handleDeactivatedPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('deactivatedPage', String(page));
    setSearchParams(params);
  };

  const isLoading = isLoadingActivated || isLoadingDeactivated;
  const hasFilters = serial || model;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-rows-[min-content_min-content_min-content_1fr] h-full space-y-6 p-6 py-10"
    >
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Ativação LoRa</h1>
          <p className="text-sm text-muted-foreground">Gerencie a ativação de chaves LoRa.</p>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="p-1 text-xs gap-0.5 px-2">
            Total de chaves disponíveis
            <span>{total}</span>
          </Badge>

          <Link to="/connectivity/lora-activation-logs">
            <Button variant="outline">
              <List />
              Logs de ativação
            </Button>
          </Link>

          <Link to="/connectivity/batch-lora-activation">
            <Button>
              <Sheet />
              Ativação/desativação em massa
            </Button>
          </Link>
        </div>
      </div>

      <Separator />

      {/* Search Form */}
      <section>
        <form onSubmit={handleSearch} className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Input
              {...form.register('serial')}
              placeholder="Pesquise pelo serial"
              className="w-64"
            />

            <Controller
              control={form.control}
              name="model"
              render={({ field }) => (
                <div className="w-64">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Selecione o modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {technologies?.map((tech) => (
                        <SelectItem key={tech.id} value={tech.name.system}>
                          {tech.name.brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />

            <Button type="submit" size="icon" variant="outline">
              <Search />
            </Button>

            {hasFilters && (
              <Button type="button" variant="outline" onClick={handleClearFilters}>
                Limpar
              </Button>
            )}
          </div>

          <div>
            <Controller
              name="view"
              control={form.control}
              render={() => (
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={view}
                  className="border border-border p-1 rounded-md bg-background"
                  onValueChange={handleViewChange}
                >
                  <ToggleGroupItem value="column" size="sm">
                    <Columns2 />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="list" size="sm">
                    <List />
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            />
          </div>
        </form>
      </section>

      {/* Content */}
      {isLoading ? (
        <section className="h-full">
          <TableSkeleton rows={10} />
        </section>
      ) : view === 'column' ? (
        // Column View - Side by Side
        <section className="grid grid-cols-2 gap-4 h-full overflow-hidden">
          <Card className="overflow-hidden grid grid-rows-[min-content_1fr]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Comunicação ativada</CardTitle>
                <CardDescription>
                  Visualize e gerencie todos os dispositivos com comunicação ativada
                </CardDescription>
              </div>
              <Badge variant="secondary" className="px-2 gap-1">
                <span>{activatedDevices?.total ?? 0}</span>
                disponíveis
              </Badge>
            </CardHeader>
            <CardContent className="!p-0 overflow-y-auto">
              {activatedDevices && (
                <DeviceActivationTable
                  data={activatedDevices}
                  activate
                  currentPage={activatedPage}
                  pageParamName="activatedPage"
                  onPageChange={handleActivatedPageChange}
                />
              )}
            </CardContent>
          </Card>

          <Card className="overflow-hidden grid grid-rows-[min-content_1fr]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Comunicação desativada</CardTitle>
                <CardDescription>
                  Visualize e gerencie todos os dispositivos com comunicação desativada
                </CardDescription>
              </div>
              <Badge variant="secondary" className="px-2 gap-1">
                <span>{deactivatedDevices?.total ?? 0}</span>
                disponíveis
              </Badge>
            </CardHeader>
            <CardContent className="!p-0 overflow-y-auto">
              {deactivatedDevices && (
                <DeviceActivationTable
                  data={deactivatedDevices}
                  activate={false}
                  currentPage={deactivatedPage}
                  pageParamName="deactivatedPage"
                  onPageChange={handleDeactivatedPageChange}
                />
              )}
            </CardContent>
          </Card>
        </section>
      ) : (
        // List View - Tabs
        <section>
          <Tabs defaultValue="activated">
            <TabsList>
              <TabsTrigger value="activated">
                Comunicação ativada ({activatedDevices?.total ?? 0})
              </TabsTrigger>
              <TabsTrigger value="deactivated">
                Comunicação desativada ({deactivatedDevices?.total ?? 0})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="activated" className="border border-border rounded-md">
              {activatedDevices && (
                <DeviceActivationTable
                  data={activatedDevices}
                  activate
                  currentPage={activatedPage}
                  pageParamName="activatedPage"
                  onPageChange={handleActivatedPageChange}
                />
              )}
            </TabsContent>
            <TabsContent value="deactivated" className="border border-border rounded-md">
              {deactivatedDevices && (
                <DeviceActivationTable
                  data={deactivatedDevices}
                  activate={false}
                  currentPage={deactivatedPage}
                  pageParamName="deactivatedPage"
                  onPageChange={handleDeactivatedPageChange}
                />
              )}
            </TabsContent>
          </Tabs>
        </section>
      )}
    </motion.div>
  );
}
